// libraries
import {
  breakpointSizes,
  FieldLabel,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import { useSelector } from 'react-redux'
import { selectPuttingOrganizationsFromState } from 'src/app/OrganizationInfo/redux/organizationInfo.selectors'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OrgNameAndIndustryForm from 'src/components/OrgNameAndIndustryForm/OrgNameAndIndustryForm'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { industryOptionsFromConfig } from 'src/queries/session/config/config.options'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import {
  createAbandonEditOrganizationAction,
  createEditOrganizationInitAction,
  putOrganization,
} from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectIsOrganizationBeingEditedFromState,
  selectOrganizationTopLevelErrorFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { PatchOrganizationRequest } from 'src/requests/patchOrganization.request'
import { css } from 'styled-components'

export type OrgNameAndIndustryBoxFields = {
  name?: string
  industry?: string
}

interface OrgNameAndIndustryBoxProps {
  user: UserListModel
  organization: OrganizationsListModel
}

const OrgNameAndIndustryBox: React.FC<OrgNameAndIndustryBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const puttingOrganization = useSelector(selectPuttingOrganizationsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Default [] preserves the legacy redux initial-state render (empty option
  // list while pending) — the config reducer had no loading/error flags.
  const { data: industryOptions = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: industryOptionsFromConfig,
  })
  const isOrganizationBeingEdited = useSelector(
    selectIsOrganizationBeingEditedFromState,
  )
  const organizationTopLevelError = useSelector(
    selectOrganizationTopLevelErrorFromState,
  )

  const dispatch = useBackgroundDispatch()

  // Local port of the removed OrganizationsListModel.getLocalizedIndustryString
  // getter: show the localized option text for the org's industry, falling
  // back to the raw industry value while options are pending or unmatched.
  const localizedIndustry =
    industryOptions.find(
      (industry) => industry.value === props.organization.industry,
    )?.details.text ?? props.organization.industry

  const dispatchPutOrganization = (
    data: Partial<PatchOrganizationRequest>,
  ): void => {
    dispatch(putOrganization(props.organization.id, data))
  }

  const onCancel = (): void => {
    dispatch(createAbandonEditOrganizationAction())
  }

  return (
    <Box noShadow outlineBorder className={'flex-column gap-7'}>
      <div className={'flex-row justify-between'}>
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('Name and Industry')}
        </span>
        {props.user.hasOrgOwnerPermissions && !isOrganizationBeingEdited && (
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
            `}
            onClick={(): void => {
              dispatch(createEditOrganizationInitAction())
            }}
            ariaLabelSource={'Update'}
            colorSvgOnHover
          >
            {translate('Update')}
            <SvgUpdate
              css={css`
                transform: scale(0.7);
                margin-left: 4px;
              `}
            />
          </HoverableLink>
        )}
      </div>
      {isOrganizationBeingEdited ? (
        <OrgNameAndIndustryForm
          industryOptions={industryOptions}
          putOrganization={dispatchPutOrganization}
          puttingOrganization={puttingOrganization}
          onCancel={onCancel}
          orgName={props.organization.name}
          orgIndustry={localizedIndustry}
          topLevelError={organizationTopLevelError}
        />
      ) : (
        <div className={'flex-column gap-8'}>
          <div
            css={css`
              ${getBreakpointUp(breakpointSizes.MD)(css`
                margin-right: 32px;
              `)}
            `}
          >
            <FieldLabel>{translate('Organization Name')}</FieldLabel>
            <div
              css={css`
                margin-top: ${spacing(1)};
                ${getFontCss(
                  16,
                  styleConstants.colors.font_gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {props.organization.name}
            </div>
          </div>
          <div>
            <FieldLabel>{translate('Industry')}</FieldLabel>
            <div
              css={css`
                margin-top: ${spacing(1)};
                ${getFontCss(
                  16,
                  styleConstants.colors.font_gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {localizedIndustry}
            </div>
          </div>
        </div>
      )}
    </Box>
  )
}

export default OrgNameAndIndustryBox
